import type { AffiliateProgramHub } from "@/services/program.service";
import { formatCurrency } from "@/utils/currency";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { money, monthYear, pct, rateText } from "./helpers";

/**
 * The deal with this brand and where the affiliate stands on its ladder.
 * The rate is the one figure; the rest is a sentence. No brand-wide numbers.
 */
export function ProgramCard({ hub }: { hub: AffiliateProgramHub }) {
  const { program, standing, tiers } = hub;
  const isFixed = program.commissionType === "FIXED";
  const rate = standing.effectiveCommissionValue;
  const viaLink = program.attributionMethod === "SNEAKYLINK";

  return (
    <DashboardCard
      id="dashboard-program-card"
      title={program.name || "Your program"}
      action={
        <span className="text-xs text-muted-foreground">Partner since {monthYear(standing.enrolledAt)}</span>
      }
      bodyClassName="px-5 pb-5"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline gap-1 leading-none">
          <span className="text-4xl font-semibold tabular-nums tracking-tight text-foreground">
            {isFixed ? money(rate) : pct(rate)}
          </span>
          <span className="text-xl font-medium text-brand-600">{isFixed ? "/ order" : "%"}</span>
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-foreground">
            {standing.currentTier ? (
              <>
                You are on the <strong>{standing.currentTier.name}</strong> tier and earn{" "}
                <strong>{rateText(rate, program.commissionType)}</strong>
              </>
            ) : (
              <>
                You earn <strong>{rateText(rate, program.commissionType)}</strong>
              </>
            )}{" "}
            on every order placed {viaLink ? "through your link" : "with your code"}. Customers get{" "}
            <strong>{pct(program.discountValue)}% off</strong>.
            {viaLink && program.sneakylinkCodeLifetime
              ? ` Each visitor's code stays valid for ${program.sneakylinkCodeLifetime} hours.`
              : ""}
          </p>
          {tiers.length > 0 && <TierTrack hub={hub} />}
        </div>
      </div>
    </DashboardCard>
  );
}

/**
 * The ladder as one line: ticks at each threshold on a scale from zero to
 * the top tier, a dot for where the affiliate is. Reads as a distance still
 * to travel rather than a list of numbers.
 */
function TierTrack({ hub }: { hub: AffiliateProgramHub }) {
  const { tiers, standing, program } = hub;
  const top = tiers[tiers.length - 1].threshold || 1;
  const scale = (v: number) => `${Math.min(100, Math.max(0, (v / top) * 100))}%`;
  const reached = standing.currentTier?.id;
  const reachedIndex = reached ? tiers.findIndex((t) => t.id === reached) : -1;

  return (
    <div className="flex flex-col gap-2">
      <div className="relative mt-1 h-6">
        <div className="absolute left-0 right-0 top-2 h-px bg-border" />
        <div className="absolute left-0 top-2 h-px bg-brand-600" style={{ width: scale(standing.totalSales) }} />
        {tiers.map((tier, i) => (
          <div key={tier.id ?? tier.name} className="absolute top-0" style={{ left: scale(tier.threshold) }}>
            <span
              aria-hidden
              className={`block h-4 w-px -translate-x-1/2 ${i <= reachedIndex ? "bg-brand-600" : "bg-border"}`}
            />
          </div>
        ))}
        <span
          aria-hidden
          className="absolute top-2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-600 ring-2 ring-card"
          style={{ left: scale(standing.totalSales) }}
        />
      </div>
      <ol className="relative flex h-8 text-[11px] leading-tight text-muted-foreground">
        {tiers.map((tier, i) => (
          <li
            key={tier.id ?? tier.name}
            className={`absolute -translate-x-1/2 whitespace-nowrap text-center ${
              i <= reachedIndex ? "text-foreground" : ""
            }`}
            style={{ left: scale(tier.threshold) }}
          >
            <span className="block font-medium">{tier.name}</span>
            <span className="block tabular-nums">{rateText(tier.commissionValue, program.commissionType)}</span>
          </li>
        ))}
      </ol>
      <p className="text-xs text-muted-foreground">
        {standing.nextTier ? (
          <>
            <span className="font-medium text-foreground">{formatCurrency(standing.remainingToNext)}</span> more
            in sales unlocks {standing.nextTier.name} at{" "}
            {rateText(standing.nextTier.commissionValue, program.commissionType)}
            {standing.nextTier.perks ? ` plus ${standing.nextTier.perks}` : ""}.
          </>
        ) : (
          <>
            Top tier reached.
            {standing.currentTier?.perks ? ` Includes ${standing.currentTier.perks}.` : ""}
          </>
        )}
      </p>
    </div>
  );
}
